
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: string;
  type: 'text' | 'image' | 'file';
  content: string;
  fileName?: string;
  fileSize?: number;
  timestamp: number;
  sender: 'user' | 'assistant';
}

export default function Workspace() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('https://www.example.com');
  const [urlInput, setUrlInput] = useState('https://www.example.com');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // 添加欢迎消息
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      type: 'text',
      content: '您好！我是智能助手，可以帮助您处理各种问题。您可以发送文字消息、上传图片或文件。',
      timestamp: Date.now(),
      sender: 'assistant'
    };
    setMessages([welcomeMessage]);
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: generateId(),
      type: 'text',
      content: inputMessage.trim(),
      timestamp: Date.now(),
      sender: 'user'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // 模拟AI回复
    setTimeout(() => {
      const assistantMessage: Message = {
        id: generateId(),
        type: 'text',
        content: `我收到了您的消息："${userMessage.content}"。这是一个智能回复示例。您可以继续与我对话或上传文件。`,
        timestamp: Date.now(),
        sender: 'assistant'
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageMessage: Message = {
        id: generateId(),
        type: 'image',
        content: event.target?.result as string,
        fileName: file.name,
        fileSize: file.size,
        timestamp: Date.now(),
        sender: 'user'
      };
      setMessages(prev => [...prev, imageMessage]);

      // 模拟AI对图片的回复
      setTimeout(() => {
        const assistantMessage: Message = {
          id: generateId(),
          type: 'text',
          content: `我看到您上传了图片"${file.name}"。这是一张很有趣的图片！我可以帮您分析图片内容或回答相关问题。`,
          timestamp: Date.now(),
          sender: 'assistant'
        };
        setMessages(prev => [...prev, assistantMessage]);
      }, 1500);
    };
    reader.readAsDataURL(file);
    
    // 清空input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileMessage: Message = {
      id: generateId(),
      type: 'file',
      content: file.name,
      fileName: file.name,
      fileSize: file.size,
      timestamp: Date.now(),
      sender: 'user'
    };
    setMessages(prev => [...prev, fileMessage]);

    // 模拟AI对文件的回复
    setTimeout(() => {
      const assistantMessage: Message = {
        id: generateId(),
        type: 'text',
        content: `我收到了您上传的文件"${file.name}"（${(file.size / 1024).toFixed(1)} KB）。我可以帮您处理这个文件或回答相关问题。`,
        timestamp: Date.now(),
        sender: 'assistant'
      };
      setMessages(prev => [...prev, assistantMessage]);
    }, 1500);

    // 清空input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLoadUrl = () => {
    if (urlInput.trim()) {
      setIframeUrl(urlInput.trim());
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 左侧iframe区域 */}
      <div className="w-4/5 bg-white border-r border-gray-200 flex flex-col">
        {/* URL输入栏 */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <i className="ri-global-line text-lg"></i>
              <span>嵌入页面</span>
            </div>
            <div className="flex-1 flex items-center space-x-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="请输入要嵌入的网页URL"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleLoadUrl()}
              />
              <button
                onClick={handleLoadUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1"></i>
                加载
              </button>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              title="返回工作台"
            >
              <i className="ri-home-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* iframe内容区域 */}
        <div className="flex-1 relative">
          <iframe
            src={iframeUrl}
            className="w-full h-full border-0"
            title="嵌入页面"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
          <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-xs">
            {iframeUrl}
          </div>
        </div>
      </div>

      {/* 右侧聊天区域 */}
      <div className="w-1/5 bg-white flex flex-col">
        {/* 聊天头部 */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <i className="ri-robot-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">智能助手</h3>
              <p className="text-xs text-gray-500">在线服务</p>
            </div>
          </div>
        </div>

        {/* 消息区域 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg ${
                  message.sender === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {message.type === 'text' && (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                )}
                
                {message.type === 'image' && (
                  <div className="space-y-2">
                    <img
                      src={message.content}
                      alt={message.fileName}
                      className="max-w-full h-auto rounded-lg"
                    />
                    <p className="text-xs opacity-75">
                      {message.fileName} ({formatFileSize(message.fileSize || 0)})
                    </p>
                  </div>
                )}
                
                {message.type === 'file' && (
                  <div className="flex items-center space-x-2">
                    <i className="ri-file-line text-lg"></i>
                    <div>
                      <p className="text-sm font-medium">{message.fileName}</p>
                      <p className="text-xs opacity-75">
                        {formatFileSize(message.fileSize || 0)}
                      </p>
                    </div>
                  </div>
                )}
                
                <p className={`text-xs mt-1 ${
                  message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                  <span className="text-sm text-gray-600">思考中...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          {/* 文件上传按钮 */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => imageInputRef.current?.click()}
              className="flex-1 p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer flex items-center justify-center space-x-1"
              title="上传图片"
            >
              <i className="ri-image-line"></i>
              <span className="text-xs">图片</span>
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer flex items-center justify-center space-x-1"
              title="上传文件"
            >
              <i className="ri-attachment-line"></i>
              <span className="text-xs">文件</span>
            </button>
          </div>

          {/* 消息输入 */}
          <div className="flex space-x-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="输入消息..."
              disabled={isLoading}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm resize-none"
              rows={2}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap self-end"
            >
              {isLoading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-send-plane-line"></i>
              )}
            </button>
          </div>
        </div>

        {/* 隐藏的文件输入 */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}
